import { type FormEvent, useState } from 'react';
import { CheckCircle2, Mail, MapPin, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { isMarketingOnlyDomain, MarketingFooter, MarketingHeader } from '@/modules/marketing/components/site-chrome';
import { useSubmitContactForm } from '@/modules/marketing/hooks/use-contact-form';
import { usePageSeo } from '@/modules/marketing/hooks/use-page-seo';

function ContactForm() {
    const submit = useSubmitContactForm();

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setErrorMessage(null);

        try {
            const result = await submit.mutateAsync({
                name,
                email,
                phone: phone || undefined,
                subject: subject || undefined,
                message,
            });
            setSuccessMessage(result.message);
        } catch {
            const supportEmail = isMarketingOnlyDomain() ? 'hello@pricingcrm.com' : 'hello@aipa.io';
            setErrorMessage(`Something went wrong sending your message — please try again, or email us directly at ${supportEmail}.`);
        }
    }

    if (successMessage) {
        return (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border bg-card p-10 text-center shadow-xl">
                <CheckCircle2 className="size-12 text-emerald-500" />
                <h3 className="text-xl font-bold tracking-tight">Message sent</h3>
                <p className="max-w-sm text-sm text-muted-foreground">{successMessage}</p>
            </div>
        );
    }

    return (
        <form className="space-y-4 rounded-2xl border bg-card p-6 shadow-xl sm:p-8" onSubmit={handleSubmit}>
            <div>
                <h2 className="text-xl font-bold tracking-tight">Send us a message</h2>
                <p className="mt-1 text-sm text-muted-foreground">Questions, feedback, or just want to say hi — we&rsquo;d love to hear from you.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="cf-name">Full name</Label>
                    <Input id="cf-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Taylor" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="cf-email">Email</Label>
                    <Input
                        id="cf-email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="jordan@company.com"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="cf-phone">Phone (optional)</Label>
                    <Input id="cf-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="cf-subject">Subject (optional)</Label>
                    <Input id="cf-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's this about?" />
                </div>
            </div>

            <div className="space-y-2">
                <Label htmlFor="cf-message">Message</Label>
                <Textarea
                    id="cf-message"
                    rows={5}
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="How can we help?"
                />
            </div>

            {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}

            <Button type="submit" size="lg" className="w-full" loading={submit.isPending}>
                Send message
            </Button>
        </form>
    );
}

const CONTACT_DETAILS = [
    { icon: Mail, label: 'Email', value: 'hello@aipa.io', href: 'mailto:hello@aipa.io' },
    { icon: Phone, label: 'Phone', value: '+1 (555) 010-0100', href: 'tel:+15550100100' },
    { icon: MapPin, label: 'Location', value: 'Remote-first, serving customers worldwide', href: undefined },
] as const;

const PRICINGCRM_CONTACT_DETAILS = [
    { icon: Mail, label: 'Email', value: 'hello@pricingcrm.com', href: 'mailto:hello@pricingcrm.com' },
    { icon: Phone, label: 'Phone', value: '+1 (555) 010-0100', href: 'tel:+15550100100' },
    { icon: MapPin, label: 'Location', value: 'Remote-first, serving customers worldwide', href: undefined },
] as const;

export function ContactPage() {
    const pricingCrm = isMarketingOnlyDomain();

    usePageSeo(
        pricingCrm
            ? {
                  title: 'Contact PricingCRM — Get in Touch',
                  description:
                      'Have a question about PricingCRM’s Lead Generator, Pricing Management, or CMA reporting? Send us a message — our team typically responds within one business day.',
                  keywords: 'contact PricingCRM, PricingCRM support, PricingCRM sales contact, pricing software support',
              }
            : {
                  title: 'Contact AIPA — Get in Touch',
                  description:
                      "Have a question about AIPA's AI Assistant, Pricing Intelligence, or CRM? Send us a message — our team typically responds within one business day.",
                  keywords: 'contact AIPA, AIPA support, AIPA sales contact, AI chatbot support',
              },
    );

    const contactDetails = pricingCrm ? PRICINGCRM_CONTACT_DETAILS : CONTACT_DETAILS;

    return (
        <div className="min-h-screen bg-background text-foreground">
            <MarketingHeader />

            <section className="relative overflow-hidden">
                <div className="absolute inset-0 -z-10 overflow-hidden">
                    <div className="absolute -left-24 -top-24 size-96 rounded-full bg-primary/10 blur-3xl" />
                    <div className="absolute -bottom-24 -right-16 size-96 rounded-full bg-sky-400/10 blur-3xl" />
                </div>

                <div className="mx-auto max-w-6xl px-6 py-16 lg:py-24">
                    <h1 className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">Get in touch</h1>
                    <p className="mx-auto mt-3 max-w-xl text-center text-sm text-muted-foreground">
                        Have a question or need help? Send us a message and our team will get back to you shortly.
                    </p>

                    <div className="mx-auto mt-10 grid max-w-6xl grid-cols-1 items-start gap-8 lg:grid-cols-2">
                        <div className="space-y-4">
                            {contactDetails.map((detail) => (
                                <div key={detail.label} className="flex items-center gap-3 rounded-xl border bg-card p-4">
                                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <detail.icon className="size-5" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-muted-foreground">{detail.label}</p>
                                        {detail.href ? (
                                            <a href={detail.href} className="text-sm font-semibold hover:underline">
                                                {detail.value}
                                            </a>
                                        ) : (
                                            <p className="text-sm font-semibold">{detail.value}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <ContactForm />
                    </div>
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
